import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ApiClient } from '../api/client';
import { getSocketFactory } from '../api/socketFactory';
import type { WsServerMessage } from '../api/types';
import { GameSocket, type ConnectionState } from '../api/ws';
import { GuestSessionStore, type GuestSession } from '../auth/guestSession';
import { config } from '../config';

type MessageListener = (msg: WsServerMessage) => void;

export interface AppServices {
  api: ApiClient;
  store: GuestSessionStore;
  session: GuestSession | null;
  guestState: 'idle' | 'creating' | 'ready' | 'failed';
  guestError: string | null;
  retryGuest(): void;
  socket: GameSocket | null;
  connection: ConnectionState;
  reconnectAttempt: number;
  onMessage(listener: MessageListener): () => void;
}

const AppContext = createContext<AppServices | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  // store / api 只建立一次；它們沒有 React state，重建會弄丟 token。
  const storeRef = useRef<GuestSessionStore | null>(null);
  if (!storeRef.current) storeRef.current = new GuestSessionStore(config.storagePrefix);
  const store = storeRef.current;

  const apiRef = useRef<ApiClient | null>(null);
  if (!apiRef.current) {
    apiRef.current = new ApiClient({
      baseUrl: config.apiBaseUrl,
      getToken: () => store.getToken(),
    });
  }
  const api = apiRef.current;

  const [session, setSession] = useState<GuestSession | null>(() => store.get());
  const [guestState, setGuestState] = useState<AppServices['guestState']>(() => (store.get() ? 'ready' : 'idle'));
  const [guestError, setGuestError] = useState<string | null>(null);
  const [connection, setConnection] = useState<ConnectionState>('idle');
  const [reconnectAttempt, setReconnectAttempt] = useState(0);

  const listenersRef = useRef(new Set<MessageListener>());
  const socketRef = useRef<GameSocket | null>(null);
  const creatingRef = useRef(false);

  const onMessage = useCallback((listener: MessageListener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  /**
   * 匿名入場（C02 §3）。第一次進站就打 POST /auth/guest —— 玩家不需要按任何東西。
   * 這是「立即開玩，免註冊」的技術實現。
   */
  const ensureGuest = useCallback(async () => {
    if (creatingRef.current) return;
    if (store.get()) {
      setSession(store.get());
      setGuestState('ready');
      return;
    }
    creatingRef.current = true;
    setGuestState('creating');
    setGuestError(null);
    try {
      const resp = await api.createGuest();
      const s = store.set(resp);
      setSession(s);
      setGuestState('ready');
    } catch (e) {
      setGuestState('failed');
      setGuestError(e instanceof Error ? e.message : String(e));
    } finally {
      creatingRef.current = false;
    }
  }, [api, store]);

  useEffect(() => {
    void ensureGuest();
  }, [ensureGuest]);

  // 有了 token 才建立 WebSocket（C02 §6：token 走 query string）。
  useEffect(() => {
    if (guestState !== 'ready' || !session) return;
    if (socketRef.current) return;

    const sock = new GameSocket(
      {
        url: config.wsUrl,
        getToken: () => store.getToken(),
        socketFactory: getSocketFactory(),
      },
      {
        onMessage: (msg) => {
          for (const fn of listenersRef.current) fn(msg);
        },
        onStateChange: (state, attempt) => {
          setConnection(state);
          setReconnectAttempt(attempt);
        },
      },
    );
    socketRef.current = sock;
    sock.connect();

    return () => {
      sock.close();
      socketRef.current = null;
    };
  }, [guestState, session, store]);

  const value = useMemo<AppServices>(
    () => ({
      api,
      store,
      session,
      guestState,
      guestError,
      retryGuest: () => void ensureGuest(),
      socket: socketRef.current,
      connection,
      reconnectAttempt,
      onMessage,
    }),
    [api, store, session, guestState, guestError, ensureGuest, connection, reconnectAttempt, onMessage],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppServices {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp 必須放在 <AppProvider> 之內');
  return ctx;
}
